import React from 'react';
import { Typography, Tag } from 'antd';
import { RobotOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { PageContext } from '../../types/agent';
import styles from './ChatEmptyState.module.css';

interface ChatEmptyStateProps {
  pageContext: PageContext;
  onPick: (prompt: string) => void;
}

// Domain prompt-starters for the seafood-store admin. Picking one populates the
// composer (and the panel can send immediately) — the highest-leverage way to
// drive a useful first message instead of a blank box.
const STARTERS: { label: string; prompt: string }[] = [
  { label: '📦 Підсумок замовлень', prompt: 'Підсумуй сьогоднішні замовлення' },
  { label: '💰 Найдорожчі товари', prompt: 'Які товари найдорожчі в каталозі?' },
  { label: '⚠️ Низькі залишки', prompt: 'Покажи товари, у яких закінчуються залишки' },
  { label: '🏆 Топ за виручкою', prompt: 'Покажи топ-5 товарів за виручкою' },
  { label: '👥 Нові клієнти', prompt: 'Скільки нових клієнтів за цей тиждень?' },
  { label: '🎟️ Активні промокоди', prompt: 'Які промокоди зараз активні?' },
];

const ChatEmptyState: React.FC<ChatEmptyStateProps> = ({ pageContext, onPick }) => {
  const filters = pageContext.filters
    ? Object.entries(pageContext.filters)
    : [];
  const selected = pageContext.selected_ids ?? [];

  return (
    <div className={styles.root}>
      <div className={styles.hero}>
        <RobotOutlined className={styles.heroIcon} />
        <Typography.Title level={5} className={styles.heroTitle}>
          Чим допомогти?
        </Typography.Title>
        <Typography.Text type="secondary">
          Оберіть підказку або напишіть запит нижче.
        </Typography.Text>
      </div>

      <div className={styles.chips}>
        {STARTERS.map((s) => (
          <button
            key={s.prompt}
            type="button"
            className={styles.chip}
            onClick={() => onPick(s.prompt)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className={styles.context}>
        <div className={styles.contextHead}>
          <AppstoreOutlined />
          <span>Контекст сторінки</span>
        </div>
        <div className={styles.contextBody}>
          <div className={styles.ctxRow}>
            <span className={styles.ctxKey}>Сторінка</span>
            <code className={styles.ctxVal}>{pageContext.route}</code>
          </div>
          {filters.length > 0 && (
            <div className={styles.ctxRow}>
              <span className={styles.ctxKey}>Фільтри</span>
              <span className={styles.ctxVal}>
                {filters.map(([k, v]) => (
                  <Tag key={k}>{`${k}: ${v}`}</Tag>
                ))}
              </span>
            </div>
          )}
          {selected.length > 0 && (
            <div className={styles.ctxRow}>
              <span className={styles.ctxKey}>Вибрано</span>
              <span className={styles.ctxVal}>{selected.length}</span>
            </div>
          )}
          <Typography.Text type="secondary" className={styles.ctxHint}>
            Асистент бачить цей контекст автоматично.
          </Typography.Text>
        </div>
      </div>
    </div>
  );
};

export default ChatEmptyState;
